import { Component, inject, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { ordersFeature } from '../../store/order.store';
import { OrderOrFulfillment, OrderPublic, OrderTotals } from '../../../../globalTypes/order.types';
import { OrderService } from '../../services/order.service';
import { filter, lastValueFrom, take, takeUntil } from 'rxjs';
import { Logger } from '../../helper/logger';
import { accountFeature } from '../../store/account.store';
import { Base } from './base-wrapper.component';
import { getTotalsFromItems } from '../../../../globalTypes/_helpers';
import { ButtonComponent } from "../layout/element/button.component";
import { LocationPublic } from '../../../../globalTypes/account.types';
import { NameTagComponent } from "../layout/element/name.tag.component";
import { blankBubbles, interactionsFeature } from '../../store/interactions.store';
import { Status } from '../../../../globalTypes/base.types';
import { authUserFeature } from '../../store/authUser.store';
import { DIGITAL_PRODUCT_LABEL } from '../../../../globalTypes/configs';
import { LOCATION_LABEL } from '../../../../globalTypes/configs';
const log = new Logger("OrderWrapperComponent");

@Component({
  selector: 'msh-order-wrapper',
  standalone: true,
  imports: [RouterModule, CommonModule, MatIconModule, ButtonComponent, NameTagComponent],
  template: `
    <div class="max-w-7xl mx-auto p-4">
      <!-- Top Navigation -->
      <nav class="text-xs md:text-base flex justify-center gap-6 border-b pb-0">
        @for (item of navItems; track item.label) {
          <a 
            [routerLink]="item.link"
            [routerLinkActive]="' font-bold text-orange-500 border-b-4 border-orange-500'"
            [class]="'py-3 px-3 relative transition-colors duration-200 ' 
              + (item.disabled ? 'opacity-50' : 'hover:text-orange-500') 
              ">
            @if (item.label === 'Cart' && (cartCount() && (cartCount() || 0) > 0)) {
              <span class=" -left-4 bubble">{{ cartCount() }}</span>
            }
            @if (item.label === 'Fulfillments' && (bubbleCount().fulfillment.size && (bubbleCount().fulfillment.size || 0) > 0)) {
              <span class=" -left-4 bubble">{{ bubbleCount().fulfillment.size }}</span>
            }
            @if (item.label === 'Purchase' && (bubbleCount().purchase.size && (bubbleCount().purchase.size || 0) > 0)) {
              <span class=" -left-4 bubble">{{ bubbleCount().purchase.size }}</span>
            }
            <span>{{ item.label }}</span>
          </a>
        }
      </nav>

      <!-- Main Layout -->
      <div class="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
        
        @if(order){
          <!-- Mobile summary toggle -->
          <div class="md:hidden">
            <button 
              class="w-full flex justify-between items-center bg-gray-100 px-4 py-2 rounded-lg"
              (click)="toggleSummary()">
              <span class="font-semibold">{{order.paymentTransactionId ? "Order # "+order.orderNumber : "Cart"}} Summary</span>
              <mat-icon>{{ summaryExpanded() ? 'expand_less' : 'expand_more' }}</mat-icon>
            </button>
              <div class="mt-2 bg-copy/15 p-4 rounded-lg">
              @if (summaryExpanded()) {
                <ng-container *ngTemplateOutlet="summaryTemplate;context:{order:order}"></ng-container>
              }
                <div class="flex justify-between pt-2 font-bold">
                  <span>Total</span>
                  <span>{{order.totalAmount | currency}}</span>
                </div>  
              </div>
          </div>
        }
        <!-- Left content -->
        <div [class]="' ' + (order ? 'md:col-span-3' : 'md:col-span-4')">
          <ng-content></ng-content>
        </div>

        @if(order){
          <!-- Desktop summary -->
          <div class="hidden md:block">
            <div class="bg-copy/15 p-4 rounded-lg">
              <ng-container *ngTemplateOutlet="summaryTemplate;context:{order:order}"></ng-container>
              <div class="flex justify-between  pt-2 font-bold">
                <span>
                  @if(isEstimate){ Est.}
                  Total
                </span>
                <span>{{order.totalAmount | currency}}</span>
              </div>
              @if(isCart && locationIdsWithItems.length > 0){
                <msh-button 
                  class="mt-5 block"
                  (onClick)="checkout($event)"
                  [cta]="'Checkout'"
                ></msh-button>
              } @else {
                <ng-container *ngTemplateOutlet="summaryRefunds;context:{order:order}"></ng-container>
              }
            </div>
          </div>
        }
      </div>
  </div>


    <!-- Template for summary for refunds -->
    <ng-template #summaryRefunds let-order="order">
      @if(order && order.fulfillments_on_order && order.fulfillments_on_order.length > 0) {
        @for(fulfillment of order.fulfillments_on_order; track fulfillment.id){
          @if(order.refunds_by_fulfillment && order.refunds_by_fulfillment[fulfillment.id] && order.refunds_by_fulfillment[fulfillment.id].refunds.length > 0){
            <div class="mt-4">
              <div class="font-bold text-orange-600 mb-2">Fulfillment #{{fulfillment.fulfillmentNumber}} refund info</div>
              @for(refund of order.refunds_by_fulfillment[fulfillment.id].refunds; track refund.id){
                <div class="mb-2 text-sm p-2 bg-back/50 rounded">
                  <div class="flex justify-between">
                    <span>Refund: </span>
                    <span>#{{refund.refundNumber}}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Status:</span>
                    <span [class]="'font-semibold ' + (refund.statusId === 'refund_completed' ? 'text-green-600' : refund.statusId === 'refund_failed' ? 'text-red-600' : 'text-orange-500')">
                      {{fulfillmentStatusMap[refund.statusId]?.label || (refund.statusId === 'refund_completed' ? 'Refunded' : refund.statusId === 'refund_failed' ? 'Failed' : 'Pending')}}
                    </span>
                  </div>
                  <div class="flex justify-between">
                    <span>Amount:</span>
                    <span [class]="'font-semibold ' + (refund.statusId === 'refund_completed' ? 'text-green-600' : 'opacity-50')">
                      {{(refund.amount)| currency}}
                    </span>
                  </div>
                </div>
              }
            </div>
          }
        }
      }
    </ng-template>
    <!-- Template for summary -->
    <ng-template #summaryTemplate let-order="order">
      <!-- Example summary structure, dynamic content can replace this -->
      @if(order){
        @if(order.orderNumber){
          <div>
            <div class=" text-lg uppercase font-bold pb-2">
              Order #{{order.orderNumber}}
            </div>
            @if(order.paymentTransactionId){
              <div class=" text-xs font-light pb-3">
                Transaction #{{order.paymentTransactionId}}
              </div>
            }
          </div>
        }
      <!-- loop locations -->
        @for (locationId of locationIdsWithItems; track locationId) {
          <div class="text-center text-sm py-4">
          @if(this.locations[locationId] && this.locations[locationId].name){
            <msh-name-tag 
              [label]="this.locations[locationId].name"
              [url]="this.locations[locationId].profileImage?.url"
              [size]="'small'"
            ></msh-name-tag>
          } @else {
            {{LOCATION_LABEL}} no longer active (<span class="text-xs">id: {{locationId}}</span>)
          }
          </div>
          <!-- loop items -->
          @if(order && order.orderItems_by_location && order.orderItems_by_location[locationId]; as items){
            @for (item of items; track item.id) {
              <div class="pb-5">
                <div class="font-bold">
                  @if(item.refundId){<span class="text-xs opacity-50">refund - </span>}
                  @if(item.listingVariation && item.listingVariation.listing && item.listingVariation.listing.name){
                    {{item.listingVariation.listing.name}}
                    @if(item.listingVariation.name && item.listingVariation.listing.name !== item.listingVariation.name){
                      <span class="font-normal">- {{item.listingVariation.name}}</span>
                    }
                  }
                </div>
                <div class="flex justify-between">
                  <div>{{item.pricePerItemAtTimeOfOrder | currency}} x {{item.quantity}}</div>
                  <div>
                    
                    {{item.pricePerItemAtTimeOfOrder * item.quantity | currency}}
                  </div>
                </div>
                @if(item.fixedShippingCost !== undefined && item.fixedShippingCost !== null){
                  <div class="flex justify-between">
                    <div class="text-xs">
                      @if(item.isDigitalDownload){
                        <span class="">{{DIGITAL_PRODUCT_LABEL}}</span>
                      }@else if(item.fixedShippingCost === 0){
                        <span class="">free</span>
                      }@else{
                        {{item.fixedShippingCost | currency}} for shipping
                      }
                    </div>
                  </div>
                }
                @if(item.totalTax && showItemizedSalesTax){
                  <div class="flex justify-between text-sm opacity-50">
                    <div>sales tax</div>
                    <div>{{item.totalTax | currency}}</div>
                  </div>
                }
              </div>
              <!-- discounts etc go here
              <div></div>
              <div></div> -->
            }
          }
        }
        <hr class="mt-4 mb-4 border-copy/15" >
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="font-medium">Sub Total</span>
            <span>{{order.subTotalAmount | currency}}</span>
          </div>
          @if(order.totalShippingAmount){
            <div class="flex justify-between">
              <span>Shipping Total</span>
              <span>{{order.totalShippingAmount | currency}}</span>
            </div>
          }
          @if(order.isFulfillment){
            @if(order.totalFixedShippingAmount){
              <div class="flex justify-between">
                <span>Shipping</span>
                <span>{{order.totalFixedShippingAmount | currency}}</span>
              </div>
            }
            @if(order.marketPlaceCommission){
              <div class="flex justify-between">
                <span>Marketplace Fees</span>
                <span>{{-order.marketPlaceCommission | currency}}</span>
              </div>
            } @else {
              <div class="flex justify-between">
                <span>Marketplace Fees</span>
                <span>uncalculated</span>
              </div>
            }
          } @else {
            @if(order.shippingTaxAmount && showItemizedSalesTax){
              <div class="flex justify-between text-sm opacity-50 relative -top-2">
                <span>tax on shipping</span>
                <span>{{order.shippingTaxAmount | currency}}</span>
              </div>
            } 
            <div class="flex justify-between">
              <span>Taxes</span>
              @if(order.totalTaxAmount || order.totalTaxAmount === 0){
                <span>{{order.totalTaxAmount | currency}}</span>
              }
            </div>
            @if(order.totalTaxAmount === undefined){
              <div class="null text-center">calculated at checkout</div>
            }
          }
          @if(order.payoutAmount){
            payoutAmount: {{order.payoutAmount}}
          }
          <hr class="my-2 border-copy/15">
        </div>
      }
    </ng-template>
  `,
})
export class OrderWrapperComponent extends Base<OrderWrapperComponent> implements OnInit, OnChanges {
  // cartCount = signal(2); // Replace with selector from store
  summaryExpanded = signal(false);
  cartCount = toSignal(inject(Store).select(ordersFeature.selectCartCount), { initialValue: 0 });
  bubbleCount = toSignal(inject(Store).select(interactionsFeature.selectBubbleAlerts), { initialValue: blankBubbles() });
  
  locations: {[locationId: string]: LocationPublic} = {}; 
  locationIdsWithItems: string[] = []
  showItemizedSalesTax = false // is const for now
  isEstimate = false;
  isBuyer = true;
  DIGITAL_PRODUCT_LABEL = DIGITAL_PRODUCT_LABEL
  LOCATION_LABEL = LOCATION_LABEL
  navItems = [
    { label: 'Fulfillments', link: '/order/fulfillment', active: true, disabled: false }, // maybe delivery
    { label: 'Purchases', link: '/order/purchase', active: false, disabled: false }, // maybe history
    { label: 'Files', link: '/order/digital-material', active: false, disabled: false },
    { label: 'Cart', link: '/order/cart', active: false, disabled: false },
  ];
  @Input() isCart = false;
  @Input() order?: OrderOrFulfillment | null;
  @Input() fulfillmentStatusMap: {[statusId: string]: Status} = {}

  constructor(
    private store: Store,
    private router: Router,
    private orderService: OrderService,
  ) {
    super();
    this.store.select(accountFeature.selectLocations).pipe(takeUntil(this.$destroy)).subscribe(locations => {
      this.locations = locations as {[locationId:string]: LocationPublic}
    })
  }
  async ngOnInit(): Promise<void> {
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if(changes['order']){
      this.order = changes['order'].currentValue; // I do not know why this needs to be here...
      console.log({order: this.order})
      if(this.order && this.order.orderItems_by_location){
        this.locationIdsWithItems = Object.keys(this.order?.orderItems_by_location)
      }
      const loc = await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(filter(l => !!l), take(1)));
      if(loc && loc.id){
        this.isBuyer = this.order?.buyerId === loc.id;
      }
      if(this.order && this.order.statusId === 'cart'){
        // this is a cart and needs it's totals added...
        this.order = JSON.parse(JSON.stringify(this.order)) as OrderOrFulfillment;
        console.log({order: this.order, taxCalculationId: this.order.taxCalculationId})
        if(!this.order.taxCalculationId ){
          this.isEstimate = true;
          const totals: OrderTotals = getTotalsFromItems(this.order.orderItems_on_order, this.order.discounts_on_order)
          log.log({totals})
          const { subTotalAmount, totalAmount, totalShippingAmount} = totals;
          this.order.totalAmount = totalAmount;
          this.order.totalShippingAmount = totalShippingAmount;
          this.order.subTotalAmount = subTotalAmount;
          this.order.totalTaxAmount = undefined;
        } else {
          this.isEstimate = false;
          log.log("Totals from DB")
        }
      }
    }
  }

  toggleSummary() {
    this.summaryExpanded.update(v => !v);
  }

  checkout(ev: any){
    this.router.navigate(['/order','checkout'])
  }
}
